/**
 * ตรรกะล้วนของหน้าจอ Customer Master — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * ฝาแฝดของ `lib/suppliers/suppliersView.ts` ด้วยเหตุผลเดียวกับที่ contract ของทั้งสอง
 * มิเรอร์กัน (D-089): สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดยไม่ต้องเรนเดอร์อะไรเลย —
 * โดยเฉพาะ **การประกอบ body** ซึ่งเป็นด่านเดียวที่ทางเขียนทั้งสองเส้นของหน้าจอนี้
 * เดินผ่าน
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่สร้าง `customer_id` หรือแก้ `customer_code` เลย** และ
 * นั่นเป็นข้อจำกัดโดยเจตนา: อันแรกเป็นของเซิร์ฟเวอร์ (D-039) ส่วนอันหลังเป็น
 * business key ที่เขียนครั้งเดียวตอนสร้าง (D-089) — Sales Order, lot และการส่งของ
 * ที่อ้างมันอยู่ต้องอ่านออกต่อไปตลอด และการกันซ้ำ Customer PO ด้วย
 * `UNIQUE(customer_id, customer_po_number)` จะไม่มีความหมายถ้า key ขยับได้
 */

import {
  CUSTOMER_STATUSES,
  type CreateCustomerBody,
  type Customer,
  type UpdateCustomerBody,
} from './types';

export interface CustomerStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * สามสถานะที่ฟอร์มเสนอให้เลือก — ตามลำดับที่ contract เขียนไว้
 *
 * ทั้งสามไม่ใช่การลบ: **ไม่มี `DELETE` ในกลุ่มนี้เลยโดยตั้งใจ** เอกสารที่อ้างลูกค้า
 * รายหนึ่งอยู่ต้อง trace กลับมาถึงเขาได้เสมอ ข้อความของแต่ละสถานะจึงต้องพูดว่ามัน
 * **ต่างกันอย่างไร** ไม่ใช่แค่บอกชื่อ — โดยเฉพาะ `INACTIVE` กับ `BLOCKED` ซึ่งอ่าน
 * เผิน ๆ เหมือนกัน แต่อันหนึ่งคือความสัมพันธ์ที่จบไปแล้ว ส่วนอีกอันคือความสัมพันธ์
 * ที่ยังอยู่แต่ถูกระงับไว้
 */
export const CUSTOMER_STATUS_DESCRIPTORS: readonly CustomerStatusDescriptor[] = [
  {
    code: 'ACTIVE',
    label: 'ใช้งานอยู่ (ACTIVE)',
    summary: 'เปิดใบสั่งขายและส่งของให้ลูกค้ารายนี้ได้ตามปกติ',
  },
  {
    code: 'INACTIVE',
    label: 'เลิกค้าขายแล้ว (INACTIVE)',
    summary:
      'ไม่ใช่การลบ — ใบสั่งขาย lot และการส่งของเดิมที่อ้างลูกค้ารายนี้ยังอ่านออกและตามสอบได้ครบทุกใบ',
  },
  {
    code: 'BLOCKED',
    label: 'ระงับไว้ (BLOCKED)',
    summary:
      'คนละความหมายกับเลิกค้าขาย — ความสัมพันธ์ยังอยู่แต่ถูกระงับไว้ การห้ามเปิดใบสั่งขายใหม่บังคับที่หน้าใบสั่งขาย (P2-4b) ไม่ใช่ที่นี่',
  },
];

/** สีของป้ายสถานะ — `ACTIVE` เท่านั้นที่เป็นสถานะ "ใช้งานได้" */
export type CustomerStatusTone = 'success' | 'neutral' | 'danger';

export function customerStatusTone(status: string): CustomerStatusTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'BLOCKED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น** */
export function customerStatusLabel(status: string): string {
  return CUSTOMER_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status;
}

/**
 * สถานะที่กล่องแก้ไขเสนอให้เลือกสำหรับลูกค้ารายหนึ่ง
 *
 * ปกติคือสามค่าของ contract แต่ถ้าลูกค้ารายนั้นถือสถานะที่หน้าจอยังไม่รู้จัก
 * (เช่นเซิร์ฟเวอร์ขยาย CHECK constraint ไปแล้วแต่หน้าจอยังไม่ตาม) ค่านั้นจะถูก
 * **เติมเข้าไปในตัวเลือกด้วย** ไม่ใช่ถูกละทิ้ง — ไม่งั้นการเปิดกล่องเพื่อแก้ชื่อ
 * อย่างเดียวจะทำให้ค่าที่เลือกไว้เด้งไปเป็น `ACTIVE` เงียบ ๆ แล้วผู้ใช้กดบันทึก
 * ปลดการระงับลูกค้าที่เขาไม่ได้ตั้งใจแตะ
 */
export function offeredCustomerStatuses(currentStatus: string): readonly string[] {
  const statuses: readonly string[] = CUSTOMER_STATUSES;
  return statuses.includes(currentStatus) ? statuses : [...statuses, currentStatus];
}

/**
 * ค่าที่ตัวกรองสถานะส่งให้ `GET /v1/customers?status=` — คืน `undefined` เมื่อ "ทุกสถานะ"
 *
 * ต่างจากช่องค้นหาซึ่งกรองฝั่งเบราว์เซอร์: `status` เป็นพารามิเตอร์ที่ contract รับจริง
 * การกรองมันฝั่งเบราว์เซอร์จะกรองแค่ **หน้าที่โหลดมาแล้ว** แล้วผู้ใช้ที่เลือก
 * "ระงับไว้" จะเห็นรายการว่างทั้งที่มีลูกค้าถูกระงับอยู่ในหน้าถัดไป
 *
 * `undefined` ไม่ใช่สตริงว่าง — คีย์ที่ไม่ได้ระบุต้องไม่เดินทางไปเลย
 */
export function toCustomerStatusFilter(status: string): string | undefined {
  const trimmed = status.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * กรองรายการลูกค้าด้วยคำค้นเดียว — เทียบทั้งรหัสและชื่อเพราะตารางแสดงทั้งสองคอลัมน์
 *
 * เจ้าหน้าที่ขายที่คัดลอกรหัสลูกค้ามาจากใบสั่งขายต้องหาลูกค้ารายนั้นเจอด้วยรหัสนั้น
 * ไม่ใช่ต้องแปลรหัสกลับเป็นชื่อก่อน
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/customers` ไม่มีพารามิเตอร์ค้นหาเลย มีแต่ `status`/`limit`/`cursor`
 */
export function filterCustomers(
  items: readonly Customer[],
  query: string,
): readonly Customer[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (customer) =>
      customer.customer_code.toLocaleLowerCase().includes(needle) ||
      customer.customer_name.toLocaleLowerCase().includes(needle),
  );
}

/**
 * ประกอบ body ของ `POST /v1/customers` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ทั้งรหัสและชื่อถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอ
 * แต่ **ติดอยู่กับ business key ตลอดไป**: `customer_code` แก้ไม่ได้หลังสร้าง (D-089)
 * รหัสที่มีช่องว่างติดมาจากการคัดลอกจึงกลายเป็นรหัสที่พิมพ์ตามไม่ได้ และเป็นรหัสที่
 * `uq` มองว่าไม่ซ้ำกับรหัสเดียวกันที่ไม่มีช่องว่าง
 *
 * `status` ที่เป็นสตริงว่างกลายเป็น `undefined` **ไม่ใช่สตริงว่างที่ถูกส่งออกไป** —
 * contract บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้
 */
export function toCreateCustomerBody(
  customerCode: string,
  customerName: string,
  status: string,
): CreateCustomerBody | null {
  const code = customerCode.trim();
  const name = customerName.trim();
  if (code === '' || name === '') {
    return null;
  }
  const trimmedStatus = status.trim();
  return {
    customer_code: code,
    customer_name: name,
    ...(trimmedStatus === '' ? {} : { status: trimmedStatus }),
  };
}

/**
 * ประกอบ body ของ `PATCH /v1/customers/{customer_id}` — **เฉพาะสิ่งที่เปลี่ยนจริง**
 *
 * คืน `null` ในสองกรณีที่ต่างกันแต่ให้ผลเดียวกันคือ "ยังไม่มีคำขอให้ส่ง":
 *  1. ชื่อว่างเปล่า — คอลัมน์นี้ `NOT NULL` และเป็นสิ่งเดียวที่คนอ่านใบสั่งขายใช้
 *     รู้ว่ากำลังขายให้ใคร
 *  2. ไม่มีอะไรเปลี่ยนจากค่าที่โหลดมา — เซิร์ฟเวอร์ยินดีรับ body ว่างและจะเขียน
 *     `audit_log` action `UPDATE` ให้ (contract ระบุว่าเขียน "เสมอ") แต่คำขอที่ไม่ได้
 *     ขออะไรเลยคือแถว audit ที่บันทึกว่าไม่มีการเปลี่ยนแปลง ซึ่งทำให้สมุดที่ผู้ตรวจสอบ
 *     ใช้อ่านยากขึ้นโดยไม่ได้อะไรกลับมา
 *
 * `customer_code` ไม่มีทางเข้ามาถึงที่นี่ได้เลย — มันไม่ใช่พารามิเตอร์ของฟังก์ชันนี้
 * และไม่ใช่คีย์ของ `UpdateCustomerBody`
 */
export function toUpdateCustomerBody(
  current: Customer,
  customerName: string,
  status: string,
): UpdateCustomerBody | null {
  const name = customerName.trim();
  if (name === '') {
    return null;
  }
  const trimmedStatus = status.trim();

  const body: UpdateCustomerBody = {
    ...(name === current.customer_name ? {} : { customer_name: name }),
    ...(trimmedStatus === '' || trimmedStatus === current.status
      ? {}
      : { status: trimmedStatus }),
  };

  return body.customer_name === undefined && body.status === undefined ? null : body;
}
